import { ErrorInfo } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createLogger } from '../utils/logger';
import { reportError } from './sentry/sentry';

const logger = createLogger('ErrorLogging');

export interface ErrorLog {
  id: string;
  timestamp: number;
  message: string;
  stack?: string;
  componentStack?: string;
  userAgent?: string;
  appVersion?: string;
  userId?: string;
  additionalInfo?: Record<string, any>;
}

class ErrorLoggingService {
  private static MAX_STORED_ERRORS = 50;
  private static STORAGE_KEY = '@error_logs';

  /**
   * Log an error from ErrorBoundary
   */
  async logError(
    error: Error,
    errorInfo?: ErrorInfo,
    additionalInfo?: Record<string, any>
  ): Promise<void> {
    try {
      const errorLog: ErrorLog = {
        id: this.generateErrorId(),
        timestamp: Date.now(),
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo?.componentStack,
        additionalInfo,
      };

      // Store error locally
      await this.storeErrorLocally(errorLog);

      // Log to console in development
      if (__DEV__) {
        logger.error('Error logged:', errorLog);
      }

      // Forward to Sentry (production error visibility). The service
      // internally scrubs secret-named fields from extra; additionalInfo
      // gets passed as `extra` so it's visible in the Sentry dashboard
      // alongside the stack trace.
      try {
        reportError(error, {
          componentStack: errorInfo?.componentStack,
          ...(additionalInfo ?? {}),
        });
      } catch {
        /* no-op — Sentry is best-effort */
      }

    } catch (loggingError) {
      logger.error('Failed to log error:', loggingError);
    }
  }

  /**
   * Log a custom error message
   */
  async logCustomError(
    message: string,
    additionalInfo?: Record<string, any>
  ): Promise<void> {
    const error = new Error(message);
    await this.logError(error, undefined, additionalInfo);
  }

  /**
   * Store error log locally for later retrieval
   */
  private async storeErrorLocally(errorLog: ErrorLog): Promise<void> {
    try {
      const existingLogs = await this.getStoredErrors();
      const updatedLogs = [errorLog, ...existingLogs].slice(
        0,
        ErrorLoggingService.MAX_STORED_ERRORS
      );

      await AsyncStorage.setItem(
        ErrorLoggingService.STORAGE_KEY,
        JSON.stringify(updatedLogs)
      );
    } catch (error) {
      logger.error('Failed to store error locally:', error);
    }
  }

  /**
   * Get all stored error logs
   */
  async getStoredErrors(): Promise<ErrorLog[]> {
    try {
      const logsJson = await AsyncStorage.getItem(
        ErrorLoggingService.STORAGE_KEY
      );
      return logsJson ? JSON.parse(logsJson) : [];
    } catch (error) {
      logger.error('Failed to retrieve stored errors:', error);
      return [];
    }
  }

  /**
   * Clear all stored error logs
   */
  async clearStoredErrors(): Promise<void> {
    try {
      await AsyncStorage.removeItem(ErrorLoggingService.STORAGE_KEY);
    } catch (error) {
      logger.error('Failed to clear stored errors:', error);
    }
  }

  /**
   * Get error count for a specific time period
   */
  async getErrorCount(hoursAgo: number = 24): Promise<number> {
    const errors = await this.getStoredErrors();
    const cutoffTime = Date.now() - hoursAgo * 60 * 60 * 1000;
    return errors.filter((error) => error.timestamp > cutoffTime).length;
  }

  /**
   * Generate unique error ID
   */
  private generateErrorId(): string {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Setup global error handlers
   */
  setupGlobalErrorHandlers(): void {
    // Handle unhandled promise rejections
    const originalPromiseRejectionHandler = global.Promise;

    // @ts-ignore - React Native specific
    if (global.ErrorUtils) {
      // @ts-ignore
      const originalGlobalHandler = global.ErrorUtils.getGlobalHandler();

      // @ts-ignore
      global.ErrorUtils.setGlobalHandler((error, isFatal) => {
        this.logError(error, undefined, { isFatal });
        originalGlobalHandler(error, isFatal);
      });
    }

    if (__DEV__) {
      logger.debug('Global error handlers setup complete');
    }
  }

  /**
   * Send error logs to backend (implement based on your API)
   */
  private async sendToBackend(errorLog: ErrorLog): Promise<void> {
    // TODO: Implement your backend error reporting
    // Example:
    // try {
    //   await fetch(`${API_URL}/errors/log`, {
    //     method: 'POST',
    //     headers: { 'Content-Type': 'application/json' },
    //     body: JSON.stringify(errorLog),
    //   });
    // } catch (error) {
    //   logger.error('Failed to send error to backend:', error);
    // }
  }

  /**
   * Get formatted error summary for debugging
   */
  async getErrorSummary(): Promise<string> {
    const errors = await this.getStoredErrors();
    const last24Hours = await this.getErrorCount(24);
    const lastWeek = await this.getErrorCount(168);

    return `
Error Summary:
- Total stored: ${errors.length}
- Last 24 hours: ${last24Hours}
- Last week: ${lastWeek}
- Most recent: ${errors[0] ? new Date(errors[0].timestamp).toLocaleString() : 'None'}
    `.trim();
  }

  /**
   * Check if app is experiencing high error rate
   */
  async isHighErrorRate(): Promise<boolean> {
    const recentErrors = await this.getErrorCount(1); // Last hour
    return recentErrors > 5; // More than 5 errors in an hour
  }
}

export default new ErrorLoggingService();
